import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Popper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  ClearOutlined,
  EmailOutlined,
  PhoneOutlined,
  SearchOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { meApi } from '../../api';
import type {
  MeColleague,
  OrgChartPerson,
  OrgChartResponse,
  OrgChartTreeNode,
} from '../../api/me';
import OrganizationFlow, {
  type OrganizationFlowNode,
} from '../../components/organization/OrganizationFlow';

function initials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function personToFlowNode(
  person: OrgChartPerson,
  children: OrganizationFlowNode[] = [],
): OrganizationFlowNode {
  return {
    id: person.worker_id,
    name: person.name,
    title: person.position,
    department: person.department,
    avatarUrl: person.avatar_url,
    email: person.email,
    phone: person.phone,
    status: person.status,
    isSelf: person.is_self ?? false,
    directReportCount: person.direct_report_count ?? 0,
    children,
  };
}

function treeToFlowNode(node: OrgChartTreeNode): OrganizationFlowNode {
  return {
    id: node.worker_id,
    name: node.name,
    title: node.position,
    department: node.department,
    avatarUrl: node.avatar_url,
    email: node.email,
    phone: node.phone,
    status: node.status,
    directReportCount: node.direct_report_count ?? 0,
    children: (node.children ?? []).map(treeToFlowNode),
  };
}

function responseToFlowRoots(data: OrgChartResponse): OrganizationFlowNode[] {
  const descendants = (data.descendants ?? []).map(treeToFlowNode);
  const self = personToFlowNode({ ...data.self, is_self: true }, descendants);
  let currentLevel = [
    self,
    ...(data.peers ?? []).map((peer) => personToFlowNode(peer)),
  ];

  const ancestors = data.ancestors ?? [];
  for (let index = ancestors.length - 1; index >= 0; index -= 1) {
    currentLevel = [personToFlowNode(ancestors[index], currentLevel)];
  }
  return currentLevel;
}

function descendantDepth(nodes: OrgChartTreeNode[] | undefined): number {
  if (!nodes?.length) return 0;
  return Math.max(...nodes.map((node) => 1 + descendantDepth(node.children)));
}

function visibleDepth(data: OrgChartResponse): number {
  return Math.min(3, Math.max(
    1,
    data.ancestors?.length ?? 0,
    descendantDepth(data.descendants),
  ));
}

const WorkerOrgChart: React.FC = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<OrgChartResponse | null>(null);
  const [depth, setDepth] = useState(2);
  const [knownMaxDepth, setKnownMaxDepth] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<MeColleague[]>([]);
  const [searching, setSearching] = useState(false);
  const [popperOpen, setPopperOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState<string | null>(null);
  const searchAnchorRef = useRef<HTMLDivElement | null>(null);

  const fetchChart = useCallback(async (requestedDepth: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await meApi.organization.chart(requestedDepth);
      setData(result);
      setKnownMaxDepth((previous) => {
        const truncated = result.truncated_above || result.truncated_below;
        if (!truncated) return visibleDepth(result);
        if (previous != null && requestedDepth >= previous) return null;
        return previous;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : t('common:feedback.errorGeneric'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchChart(depth);
  }, [depth, fetchChart]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setSearching(false);
      setPopperOpen(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    const handle = window.setTimeout(async () => {
      try {
        const rows = await meApi.colleagues.search(trimmed, 10);
        if (!cancelled) {
          setResults(rows);
          setPopperOpen(true);
        }
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 200);

    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [query]);

  const flowRoots = useMemo(
    () => (data ? responseToFlowRoots(data) : []),
    [data],
  );
  const maxUsefulDepth = knownMaxDepth ?? 3;

  useEffect(() => {
    if (knownMaxDepth != null && depth > knownMaxDepth) setDepth(knownMaxDepth);
  }, [depth, knownMaxDepth]);

  const inChartIds = useMemo(() => {
    const ids = new Set<string>();
    const visit = (node: OrganizationFlowNode) => {
      ids.add(node.id);
      node.children.forEach(visit);
    };
    flowRoots.forEach(visit);
    return ids;
  }, [flowRoots]);

  const highlightedIds = useMemo(
    () => (selectedWorkerId ? [selectedWorkerId] : []),
    [selectedWorkerId],
  );

  const statusLabels = useMemo(() => ({
    former: t('common:nav.former'),
    terminated: t('common:nav.former'),
    suspended: t('common:topNav.planStatus.suspended'),
  }), [t]);

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setPopperOpen(false);
    setSelectedWorkerId(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1.5,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('worker:orgChart.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('worker:orgChart.chartSubtitle')}
          </Typography>
        </Box>
        <Box ref={searchAnchorRef}>
          <TextField
            size="small"
            placeholder={t('worker:orgChart.placeholderSearch')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onFocus={() => results.length > 0 && setPopperOpen(true)}
            sx={{ width: { xs: '100%', sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: query ? (
                <InputAdornment position="end">
                  {searching ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={clearSearch}
                      aria-label={t('common:actions.clear')}
                      sx={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        p: 0,
                        color: 'text.secondary',
                      }}
                    >
                      <ClearOutlined sx={{ fontSize: 16 }} />
                    </Box>
                  )}
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
      </Box>

      <Box sx={{ width: '100%', mb: 2 }}>
        <ToggleButtonGroup
          value={depth}
          exclusive
          size="small"
          onChange={(_, value) => value != null && setDepth(value)}
          aria-label={t('worker:orgChart.ariaLevels')}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              borderRadius: 1,
              px: 1.75,
              fontSize: '0.8rem',
            },
          }}
        >
          <ToggleButton value={1} aria-label={t('worker:orgChart.ariaDirect')}>
            {t('worker:orgChart.directOnly')}
          </ToggleButton>
          <ToggleButton
            value={2}
            disabled={maxUsefulDepth < 2}
            aria-label={t('worker:orgChart.ariaExtended')}
          >
            {t('worker:orgChart.extended')}
          </ToggleButton>
          <ToggleButton
            value={3}
            disabled={maxUsefulDepth < 3}
            aria-label={t('worker:orgChart.ariaAll')}
          >
            {t('worker:orgChart.allLevels')}
          </ToggleButton>
        </ToggleButtonGroup>
        {maxUsefulDepth === 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
            {t('worker:orgChart.fullVisible')}
          </Typography>
        )}
      </Box>

      <Popper
        open={popperOpen && Boolean(query.trim())}
        anchorEl={searchAnchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={() => setPopperOpen(false)}>
          <Paper
            elevation={0}
            sx={{
              mt: 0.5,
              width: { xs: 320, sm: 380 },
              maxHeight: 360,
              overflowY: 'auto',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {results.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.disabled">
                  {t('common:form.noResults')}
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }} />}>
                {results.map((result) => {
                  const isInChart = inChartIds.has(result.worker_id);
                  return (
                    <Box
                      key={result.worker_id}
                      role={isInChart ? 'button' : undefined}
                      tabIndex={isInChart ? 0 : undefined}
                      onClick={() => {
                        if (isInChart) {
                          setSelectedWorkerId(result.worker_id);
                          setPopperOpen(false);
                        }
                      }}
                      onKeyDown={(event) => {
                        if (isInChart && (event.key === 'Enter' || event.key === ' ')) {
                          event.preventDefault();
                          setSelectedWorkerId(result.worker_id);
                          setPopperOpen(false);
                        }
                      }}
                      sx={{
                        px: 1.5,
                        py: 1,
                        display: 'flex',
                        gap: 1,
                        alignItems: 'center',
                        cursor: isInChart ? 'pointer' : 'default',
                        '&:hover': isInChart ? { bgcolor: 'action.hover' } : undefined,
                      }}
                    >
                      <Avatar
                        src={result.avatar_url ?? undefined}
                        sx={{ width: 32, height: 32, fontSize: '0.7rem', bgcolor: 'primary.main' }}
                      >
                        {initials(result.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.825rem' }} noWrap>
                            {result.name}
                          </Typography>
                          {isInChart && (
                            <Chip
                              label={t('worker:orgChart.inChart')}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 16, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.5 } }}
                            />
                          )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }} noWrap>
                          {result.position ?? '—'}
                          {result.department ? ` · ${result.department}` : ''}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.25 }} flexWrap="wrap" useFlexGap>
                          {result.email && (
                            <Box
                              component="a"
                              href={`mailto:${result.email}`}
                              onClick={(event) => event.stopPropagation()}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: 'primary.main', textDecoration: 'none' }}
                            >
                              <EmailOutlined sx={{ fontSize: 12 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{result.email}</Typography>
                            </Box>
                          )}
                          {result.phone && (
                            <Box
                              component="a"
                              href={`tel:${result.phone}`}
                              onClick={(event) => event.stopPropagation()}
                              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, color: 'text.secondary', textDecoration: 'none' }}
                            >
                              <PhoneOutlined sx={{ fontSize: 12 }} />
                              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{result.phone}</Typography>
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, width: '100%' }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && !loading && (
        <Paper
          elevation={0}
          sx={{ width: '100%', p: 3, borderRadius: 2, border: '1px solid', borderColor: 'error.main' }}
        >
          <Typography variant="body2" color="error">{error}</Typography>
        </Paper>
      )}

      {data && !loading && flowRoots.length > 0 && (
        <OrganizationFlow
          roots={flowRoots}
          highlightedIds={highlightedIds}
          selfLabel={t('worker:orgChart.youAreHere')}
          statusLabels={statusLabels}
          ariaLabel={t('worker:orgChart.title')}
          height={560}
        />
      )}

      {data && !loading && flowRoots.length === 0 && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: 5,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('worker:orgChart.noManagerOrReports')}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default WorkerOrgChart;
